import { rest } from '@0xtorch/core'
import { type ZodType, type ZodTypeDef, z } from 'zod'

export type SubscanClient = {
  post: <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
  >(parameters: {
    path: string
    body: string
    resultSchema: ZodType<Output, Def, Input>
    headers?: Record<string, string>
  }) => Promise<Output>
}

type CreateSubscanClientParameters = {
  networkName: string
  apiKey?: string
  proxyUrl?: string
}

export const createSubscanClient = ({
  networkName,
  apiKey,
  proxyUrl,
}: CreateSubscanClientParameters): SubscanClient => {
  // // apiKey = undefined かつ proxyUrl = undefined の場合はエラー
  // if (apiKey === undefined && proxyUrl === undefined) {
  //   throw new Error('apiKey or proxyUrl must be provided')
  // }

  const apiBaseUrl = `https://${networkName}.api.subscan.io`
  const rateLimitPerSecond = 1
  const requestTimestamps: number[] = []

  const post = async <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
  >(
    {
      path,
      body,
      resultSchema,
      headers,
    }: {
      path: string
      body: string
      resultSchema: ZodType<Output, Def, Input>
      headers?: Record<string, string>
    },
    repeat = 0,
  ): Promise<Output> => {
    // rate limit を超えていたら 1 秒待つ
    const time1SecondAgo = Date.now() - 1000
    if (
      requestTimestamps.filter((t) => t >= time1SecondAgo).length >=
      rateLimitPerSecond
    ) {
      await new Promise<void>((resolve) => {
        const intervalId = setInterval(() => {
          const time1SecondAgo = Date.now() - 1000
          if (
            requestTimestamps.filter((t) => t >= time1SecondAgo).length <
            rateLimitPerSecond
          ) {
            clearInterval(intervalId)
            resolve()
            return
          }
        }, 1000)
      })
    }

    // requestTimestamps に追加
    requestTimestamps.push(Date.now())

    // // define the schema for the response
    const responseSchema = z.object({
      code: z.number().int(),
      message: z.string(),
      generated_at: z.number().int(),
      data: resultSchema,
    })

    // request
    try {
      const url = new URL(path, apiBaseUrl)
      console.debug(`[POST] ${url.toString()}`)
      const { message, data } = await rest(
        proxyUrl === undefined
          ? url.toString()
          : `${proxyUrl}${encodeURIComponent(url.toString())}`,
        {
          schema: responseSchema,
          fetchOptions: {
            method: 'POST',
            headers: {
              ...headers,
              'Content-Type': 'application/json',
              ...(apiKey === undefined ? {} : { 'X-API-Key': apiKey }),
            },
            body,
          },
        },
      )
      if (message !== 'Success') {
        throw new Error(message)
      }
      return data as Output
    } catch (error) {
      if (repeat < 3 && error instanceof Error) {
        if (error.message.includes('429')) {
          console.debug(error)
          await new Promise((resolve) => setTimeout(resolve, 10_000))
          return await post({ path, body, resultSchema, headers }, repeat + 1)
        }
      }
      throw error
    }
  }

  return {
    post,
  }
}
